import { Record, RecordSubscription } from "pocketbase";
import { getCacheSongs, getSongs } from "../api/song";
import { autoFit, getCurrentSongId, isMobile, normalize } from "../helpers";
import {
    changeSessionSong,
    endSession,
    newSession,
    subscribeToSession,
    unsubscribeFromSession,
} from "../pocket/session";

type id = string;

export interface song {
    id: id;
    title: string;
    author: string;
    capo: number;
    sfw: boolean;
    international: boolean;
    genre?: string[];
    verses: any;
}

class ZpevnikDataModel {
    updateCallbacks: (() => any)[] = [];
    state = {
        navigate: (to: string) => {},
        songs: [],
        settings: {
            columns: 3,
            transpose: 0,
            fontSize: 20,
            lineBreak: true,

            darkMode: false,
            oledMode: false,
            compactMode: false,
            sortAlphabetically: true,
            swipeGesturesPrevNext: true,

            displayChords: true,
            chords: "guitar",
            transposeCapo: false,
            preferedScale: 0,

            favorites: [],
            showYTButton: false,
            showFavorites: false,
            showColumnsButton: true,
            showAutoFitButton: true,
            showFontSizeButton: false,
            showTranspositionButton: true,
            showRandomSongButton: true,
            showLineBreakButton: false,

            resetTranspositions: true,
            closeSidebarOnSongClickPC: false,
            displayNsfw: false,
            onlyInternational: false,
            autofitIntervalDelay: 10,
            maxColumns: 3,
            separateFavorites: false,

            autofitOnSessionUpdate: false,
            autofitShowTitle: false,

            yeet: false,
            ponify: false,

            editorMode: "verses",
            secret: "ahoj",
            showEditButton: false,
        },
        session: {
            online: false,
            code: "",
            leader: false,
        },
        //the font size auto fit picked, and for which page - kept out of the
        //settings so it is never saved and ends when the user moves on
        fit: null as { songId: string; fontSize: number; layout: string } | null,
    };
    //bumped whenever a fit starts or ends, so a stale fit loop stops itself
    fitRun = 0;
    //whether the fit still matched the layout at the last resize check
    fitWasCurrent = false;
    //a session update moved to this song and wants it fitted - that has to wait
    //until the song is on screen, measuring before would size the old one
    fitWhenShown: string | null = null;

    //set update callback function
    addUpdateCallback(fn: () => void) {
        this.updateCallbacks.push(fn);
    }

    //call update callback function to notify listeners
    notifyListeners() {
        this.updateCallbacks.forEach((fn) => fn());
    }

    //update model properties
    setState(data: object) {
        this.state = { ...this.state, ...data };

        this.notifyListeners();
    }

    //update settings in state
    setSettings(settings: object) {
        //picking a font size by hand replaces an auto fit
        if ("fontSize" in settings && this.state.fit) this.clearFit();
        this.setState({
            settings: {
                ...this.state.settings,
                ...settings,
            },
        });
        localStorage.setItem("settings", JSON.stringify(this.settings));
    }

    //on route change
    onRouteChanged() {
        if (this.state.settings.resetTranspositions) this.setSettings({ transpose: 0 });
        //an auto fit only lasts for the song it was made for
        if (this.state.fit && this.state.fit.songId !== getCurrentSongId()) this.clearFit();
        //the user went somewhere else before the session's song showed up
        if (this.fitWhenShown && this.fitWhenShown !== getCurrentSongId()) this.fitWhenShown = null;
    }

    //the song page calls this once it has put a song on screen
    onSongShown(id?: id) {
        if (!id || id !== this.fitWhenShown) return;
        this.fitWhenShown = null;
        this.autoFit();
    }

    //load songs from local storage, then attempt to load from server
    async loadSongs() {
        const cache = getCacheSongs();
        if (Array.isArray(cache)) this.setState({ songs: cache });

        getSongs().then((res) => {
            this.setState({ songs: res });
        });
    }

    //load settings from local storage
    loadSettings() {
        const settings = JSON.parse(localStorage.getItem("settings") || "false");
        if (settings) this.setSettings(settings);
        //a fresh phone starts at one column - three is unreadable that narrow -
        //but it stays changeable from the toolbar, unlike a hard css cap
        else if (isMobile()) this.setSettings({ columns: 1 });
    }

    //load session data
    loadSession() {
        const session = JSON.parse(localStorage.getItem("session") || "false");
        if (session) this.setState({ session: session });
        if (this.state.session.online && !this.state.session.leader) {
            subscribeToSession(this.state.session.code, (e: RecordSubscription<Record>) =>
                this.onSessionUpdate(e),
            ).catch((err) => {
                this.leaveSession();
            });
        }
    }

    constructor() {
        this.loadSettings();
        this.loadSongs();
        this.loadSession();
    }

    //get settings
    get settings(): { [key: string]: any } {
        return this.state.settings;
    }

    setting(name: string) {
        return this.settings[name];
    }

    //get songs sorted and filtered depending on settings
    get songs() {
        let set = this.state.settings;
        let favorites: string[] = set.favorites || [];
        let songs: song[] = this.state.songs || [];

        //if (!set.displayNsfw)
        //    songs = songs.filter(s => s.sfw)
        if (set.onlyInternational) songs = songs.filter((s) => s.international);
        if (set.sortAlphabetically) songs = songs.sort((a, b) => a.title.localeCompare(b.title));
        if (set.showFavorites && !set.separateFavorites)
            songs = songs.sort((a, b) =>
                favorites.includes(b.id) ? (favorites.includes(a.id) ? 0 : 1) : favorites.includes(a.id) ? -1 : 0,
            );

        return songs;
    }

    //get sorted song ids
    get songIDs(): id[] {
        return this.songs.map((song) => song.id);
    }

    //get songs whose title+author contain query string
    //strings are normalized
    searchSongs(query: string) {
        return this.songs.filter((s) => (normalize(s.title) + normalize(s.author)).includes(normalize(query)));
    }

    //get song by id from all songs, filtered out ones included - hiding a song
    //keeps it out of the lists, but a link, a session leader or the editor
    //still has to be able to open it
    getSongById(id: id) {
        const songs: song[] = this.state.songs || [];
        return songs.find((song) => song.id === id);
    }

    //get song by id in window URI
    get currentSong(): song | undefined {
        let id = getCurrentSongId();
        return this.getSongById(id);
    }

    isFavorite(id: id) {
        const favorites: id[] = model.settings.favorites;
        return favorites.includes(id);
    }

    //"playing" a capo means transposing the chords up by its fret count so the
    //open shapes sound right without one - toggle that compensation on and off.
    //it rides on the normal transpose, so it and the toolbar slider are one and
    //the same and a route change resets both
    toggleCapo(song?: song) {
        const target = song || this.currentSong;
        if (!target || !target.capo) return;
        //some songs store the capo as a negative "tune down" value - fold it into
        //0..11 (the same pitch class) so the transpose never goes negative, which
        //would index the note table out of bounds and blank the page
        const capo = (((target.capo % 12) + 12) % 12);
        const engaged = (((this.settings.transpose % 12) + 12) % 12) === capo;
        this.setSettings({ transpose: engaged ? 0 : capo });
    }

    onSessionUpdate(event: RecordSubscription<Record>) {
        const movesSong = event.action === "update" && event.record.song !== getCurrentSongId();
        if (event.action === "update") this.state.navigate(event.record.song);
        if (event.action === "delete") this.leaveSession();
        if (this.settings.autofitOnSessionUpdate) {
            if (movesSong) this.fitWhenShown = event.record.song;
            else this.autoFit();
        }
    }

    async startSession() {
        newSession()
            .then((code) => {
                localStorage.setItem("session", JSON.stringify(this.state.session));
                this.setState({
                    session: {
                        code: code,
                        online: true,
                        leader: true,
                    },
                });
                localStorage.setItem("session", JSON.stringify(this.state.session));
            })
            .catch((err) => {
                alert("Error starting new session.");
            });
    }

    async joinSession() {
        const code = prompt("Session code:")?.toUpperCase();
        if (!code) return;
        subscribeToSession(code, (e: RecordSubscription<Record>) => this.onSessionUpdate(e))
            .then(() => {
                this.setState({
                    session: {
                        online: true,
                        leader: false,
                        code: code,
                    },
                });
                localStorage.setItem("session", JSON.stringify(this.state.session));
            })
            .catch((err) => {
                alert("Error joining session.");
            });
    }

    async broadcastSong() {
        changeSessionSong(this.state.session.code, getCurrentSongId()).catch((err) => {
            alert("Session error.");
        });
    }

    async leaveSession() {
        if (this.state.session.leader) {
            endSession(this.state.session.code);
        } else {
            unsubscribeFromSession();
        }

        this.setState({
            session: {
                online: false,
                leader: false,
                code: "",
            },
        });
        localStorage.setItem("session", JSON.stringify(this.state.session));
    }

    ////
    //the font size the song is shown at: an auto fit made for this page,
    //otherwise the user's own size
    get fontSize(): number {
        return this.isFitted ? this.state.fit!.fontSize : this.settings.fontSize;
    }

    get isFitted(): boolean {
        return !!this.state.fit && this.state.fit.songId === getCurrentSongId();
    }

    //what a fit is made for besides the song: the page width and the settings
    //that reflow the lyrics. offsetWidth, so a scrollbar coming and going does
    //not count as a change
    fitLayout(): string {
        const s = this.settings;
        const width = document.getElementById("main")?.offsetWidth;
        return [width, s.columns, s.lineBreak, s.autofitShowTitle, s.displayChords, s.compactMode].join();
    }

    //a fit still made for what is on screen - after a layout change (a phone
    //turned, the sidebar opened, other columns) the fitted size stays, but the
    //button fits again instead of undoing, as it always did
    get isFitCurrent(): boolean {
        return this.isFitted && this.state.fit!.layout === this.fitLayout();
    }

    autoFit() {
        autoFit();
    }

    //the auto fit button and ctrl+e - fit the song, or undo the fit
    toggleAutoFit() {
        if (this.isFitCurrent) this.clearFit();
        else this.autoFit();
    }

    //the page area was resized - redraw only when that makes the fit current or
    //out of date, since resizes come every frame while the sidebar slides
    onMainResized() {
        const current = this.isFitCurrent;
        if (current === this.fitWasCurrent) return;
        this.fitWasCurrent = current;
        this.notifyListeners();
    }

    //start a fit from the size on screen; returns the run the fit loop must
    //keep matching to go on
    startFit() {
        this.fitRun++;
        this.fitWasCurrent = true;
        this.setState({ fit: { songId: getCurrentSongId(), fontSize: this.fontSize, layout: this.fitLayout() } });
        return this.fitRun;
    }

    setFitFontSize(fontSize: number) {
        if (this.state.fit) this.setState({ fit: { ...this.state.fit, fontSize } });
    }

    //back to the user's own font size, stopping a fit that is still running
    clearFit() {
        this.fitRun++;
        this.fitWasCurrent = false;
        if (this.state.fit) this.setState({ fit: null });
    }
}

declare global {
    interface Window {
        model: any;
    }
}

const model = new ZpevnikDataModel();
window.model = model;
export default model;
